import { Header, Section } from "../../../components/layout";
import type { IPolicy } from "../../../types";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";

import { Button, Popover, TableCell, TableRow } from "../../../components/util";
import { InviteUser, PaginatedResults } from "../../../components/content";
import organisationService from "../../../services/organisation/organisation.service";
import { invitationStatus } from "../../../helpers";
import { formatDateTime } from "../../../helpers/dateTime";

import InvitationService, {
  Invitation,
} from "../../../services/invitation/invitation.service";

import * as Styled from "./styles.css";

type InvitationsTabProps = {
  policy: IPolicy;
};

type InvitationsListProps = {
  policy: IPolicy;
};

const InvitationsTab = ({ policy }: InvitationsTabProps) => {
  const history = useHistory();

  const createInvitation = () =>
    history.push(`/invitations/policy-holder/invite?id=${policy.id}`, {
      type: "client",
    });

  const InviteButton = ({ policy }: { policy: IPolicy }) => {
    switch (invitationStatus(policy)) {
      case "doNotInvite":
        return (
          <Button color="accent" variant="contained" disabled>
            Invitations disabled
          </Button>
        );
      case "invite":
        return (
          <Button color="accent" onClick={createInvitation} variant="contained">
            Invite client
          </Button>
        );
      case "invited":
      default:
        return null;
    }
  };

  return (
    <>
      <Header
        title="Invitations"
        titleIcon="mail"
        titleIconPosition="start"
        type="section"
        actions={<InviteButton policy={policy} />}
      />

      <Section>
        <InvitationsList policy={policy} />
      </Section>
    </>
  );
};

const InvitationsList = ({ policy }: InvitationsListProps) => {
  const { search } = useLocation();

  const urlParams: Record<string, any> = useMemo(
    () => queryString.parse(search),
    [search],
  );

  const [searchTerm, setSearchTerm] = useState<string>(
    urlParams?.searchTerm ? urlParams?.searchTerm : "",
  );

  useEffect(() => {
    setSearchTerm(urlParams?.searchTerm);
  }, [urlParams?.searchTerm]);

  const invitationsRow = (invitationsSlice: Invitation[]) => {
    return (
      <>
        {invitationsSlice.map((invitation: Invitation) => (
          <TableRow key={invitation.id}>
            <TableCell>
              <Styled.Link
                href={`mailto:${invitation.email}`}
                title={invitation.email}
              >
                {invitation.email}
              </Styled.Link>
            </TableCell>
            <TableCell>{invitation.sender?.email}</TableCell>
            <TableCell>{invitation.status}</TableCell>
            <TableCell>{formatDateTime(invitation.created_at)}</TableCell>
            <TableCell>
              <OverflowMenuButton policy={policy} invitation={invitation} />
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <PaginatedResults
      id={`portfolio-client-${policy.policy_number}`}
      searchTerm={searchTerm}
      searchService={InvitationService.searchByPolicyNumber}
      service={InvitationService.getByPolicyNumber}
      serviceParams={{ policyNumber: policy.policy_number }}
      tableColumns={[
        { title: "Invitee", expand: true },
        { title: "Inviter", expand: true },
        { title: "Status", collapse: true },
        { title: "Created", collapse: true },
        { title: "Actions", collapse: true },
      ]}
      tableRows={invitationsRow}
      missing={{
        icon: "checklist",
        type: "invitations",
        message: "This is where we'll show all invitations.",
      }}
    />
  );
};

const openPolicyLink = (policy_id: string, policy_number: string) => {
  return organisationService.getPolicyLink(policy_id, policy_number);
};

const OverflowMenuButton = ({
  policy,
  invitation,
}: {
  policy: IPolicy;
  invitation: Invitation;
}) => {
  const canResend = invitation.status === "active";
  const canCancel = invitation.status !== "cancelled";
  const canViewPolicy = policy.type !== "wc";
  const isPopupEnabled = canResend || canCancel || canViewPolicy;

  return (
    <Popover
      alignment="end"
      offsetX={40}
      offsetY={-25}
      trigger={
        <Styled.MoreOptionIconButton
          icon="kebab-horizontal"
          disabled={!isPopupEnabled}
        >
          Pop
        </Styled.MoreOptionIconButton>
      }
    >
      <Styled.MoreOptionSelectMenu>
        {canResend && (
          <InviteUser
            action="resend"
            context="overflow"
            invitation={invitation}
            policyId={policy.id}
            type="client"
          />
        )}
        {canCancel && (
          <InviteUser
            action="cancel"
            context="overflow"
            invitation={invitation}
            policyId={policy.id}
            type="client"
          />
        )}
        {canViewPolicy && (
          <Styled.MoreOptionSelectOption>
            <Styled.LinkButton
              onClick={event => {
                event.preventDefault();
                const link = openPolicyLink(
                  policy.policy_id,
                  policy.policy_number || "",
                );
                window.open(link);
              }}
              href={openPolicyLink(
                policy.policy_id,
                policy.policy_number || "",
              )}
            >
              View policy details
              <Styled.MoreOptionLaunchIconButton icon="link-external" />
            </Styled.LinkButton>
          </Styled.MoreOptionSelectOption>
        )}
      </Styled.MoreOptionSelectMenu>
    </Popover>
  );
};

export default InvitationsTab;
